import { useEffect, useId, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@shared/lib/utils';
import { Button } from '../primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '../overlay/popover';
import { Text } from '../typography/text';

type DateRangeQuickFilter = {
  id: string;
  label: string;
};

type DateRangePickerLabels = {
  previous: string;
  next: string;
  clear: string;
  quickFiltersTitle: string;
  monthPickerLabel: string;
  monthSelectLabel?: string;
  yearSelectLabel?: string;
  modeSectionTitle?: string;
};

type DateRangePickerModeOption = {
  id: string;
  label: string;
};

type DateRangePickerDateContext = {
  date: Date;
  dateKey: string;
  selectedStart: string | null;
  selectedEnd: string | null;
  draftStart: string | null;
  draftEnd: string | null;
};

type DateRangePickerResolvedRange = {
  start: string;
  end: string;
  close?: boolean;
  preview?: boolean;
};

type DateRangePickerProps = {
  label: string;
  modeLabel?: string;
  locale: string;
  minDate: string | null;
  maxDate: string | null;
  selectedStart?: string | null;
  selectedEnd?: string | null;
  quickFilters: DateRangeQuickFilter[];
  activeQuickFilterId?: string | null;
  clearActive?: boolean;
  showClear?: boolean;
  onRangeSelect: (start: string, end: string) => void;
  onQuickFilterSelect: (filterId: string) => void;
  onClear: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  disabled?: boolean;
  labels: DateRangePickerLabels;
  weekStartsOn?: 1;
  className?: string;
  triggerClassName?: string;
  popoverClassName?: string;
  modeOptions?: DateRangePickerModeOption[];
  activeModeId?: string;
  onModeSelect?: (modeId: string) => void;
  isDateSelectable?: (context: DateRangePickerDateContext) => boolean;
  resolveRangeForDate?: (
    context: DateRangePickerDateContext
  ) => DateRangePickerResolvedRange | null;
  closeOnRangeSelect?: boolean;
};

const DAYS_IN_WEEK = 7;
const CALENDAR_WEEKS = 6;
const TOTAL_DAYS = DAYS_IN_WEEK * CALENDAR_WEEKS;
const MONTH_LABEL_REFERENCE_YEAR = 2024;
const WEEKDAY_LABEL_REFERENCE_SUNDAY = { year: 2024, month: 0, day: 7 } as const;
const DEFAULT_YEAR_OPTION_LOOKAROUND = 25;
const DEFAULT_MONTH_SELECT_LABEL = 'Month';
const DEFAULT_YEAR_SELECT_LABEL = 'Year';
const DEFAULT_MODE_SECTION_TITLE = 'Period mode';

const toUtcDate = (year: number, month: number, day: number) =>
  new Date(Date.UTC(year, month, day));

const addUtcDays = (date: Date, days: number) =>
  toUtcDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days);

const startOfUtcWeek = (date: Date, weekStartsOn: number) => {
  const day = date.getUTCDay();
  const diff = (day - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  return addUtcDays(date, -diff);
};

const toIsoDate = (date: Date) => date.toISOString().split('T')[0] ?? '';

const parseIsoDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isSameDay = (a: Date | null, b: Date | null) => {
  if (!a || !b) return false;
  return toIsoDate(a) === toIsoDate(b);
};

const getMonthLabel = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);

const getMonthOptions = (locale: string) => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });

  return Array.from({ length: 12 }, (_, monthIndex) => ({
    value: String(monthIndex),
    label: formatter.format(toUtcDate(MONTH_LABEL_REFERENCE_YEAR, monthIndex, 1)),
  }));
};

const getCalendarMonthStart = (date: Date) =>
  toUtcDate(date.getUTCFullYear(), date.getUTCMonth(), 1);

const getCalendarMonthForParts = (year: number, monthIndex: number) =>
  toUtcDate(year, monthIndex, 1);

const isCalendarMonthInBounds = (
  year: number,
  monthIndex: number,
  minDate: Date | null,
  maxDate: Date | null
) => {
  const monthStart = getCalendarMonthForParts(year, monthIndex);
  const minMonth = minDate ? getCalendarMonthStart(minDate) : null;
  const maxMonth = maxDate ? getCalendarMonthStart(maxDate) : null;

  return (!minMonth || monthStart >= minMonth) && (!maxMonth || monthStart <= maxMonth);
};

const getClampedCalendarMonth = (date: Date, minDate: Date | null, maxDate: Date | null) => {
  const monthStart = getCalendarMonthStart(date);
  const minMonth = minDate ? getCalendarMonthStart(minDate) : null;
  const maxMonth = maxDate ? getCalendarMonthStart(maxDate) : null;

  if (minMonth && monthStart < minMonth) {
    return minMonth;
  }

  if (maxMonth && monthStart > maxMonth) {
    return maxMonth;
  }

  return monthStart;
};

const getWeekdayLabels = (locale: string, weekStartsOn: number) => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const baseSunday = toUtcDate(
    WEEKDAY_LABEL_REFERENCE_SUNDAY.year,
    WEEKDAY_LABEL_REFERENCE_SUNDAY.month,
    WEEKDAY_LABEL_REFERENCE_SUNDAY.day
  );
  return Array.from({ length: DAYS_IN_WEEK }, (_, index) => {
    const offset = (weekStartsOn + index) % DAYS_IN_WEEK;
    return formatter.format(addUtcDays(baseSunday, offset));
  });
};

export function DateRangePicker({
  label,
  modeLabel,
  locale,
  minDate,
  maxDate,
  selectedStart,
  selectedEnd,
  quickFilters,
  activeQuickFilterId,
  clearActive,
  showClear = true,
  onRangeSelect,
  onQuickFilterSelect,
  onClear,
  onPrevious,
  onNext,
  previousDisabled,
  nextDisabled,
  disabled = false,
  labels,
  weekStartsOn = 1,
  className,
  triggerClassName,
  popoverClassName,
  modeOptions = [],
  activeModeId,
  onModeSelect,
  isDateSelectable,
  resolveRangeForDate,
  closeOnRangeSelect = false,
}: DateRangePickerProps) {
  const pickerId = useId();
  const monthSelectId = `${pickerId}-month`;
  const yearSelectId = `${pickerId}-year`;
  const [open, setOpen] = useState(false);
  const [monthYearPickerOpen, setMonthYearPickerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<{ start: string; end: string } | null>(null);
  const minDateParsed = useMemo(() => parseIsoDate(minDate), [minDate]);
  const maxDateParsed = useMemo(() => parseIsoDate(maxDate), [maxDate]);
  const activeSelectedStart = draftRange?.start ?? selectedStart ?? null;
  const activeSelectedEnd = draftRange?.end ?? selectedEnd ?? null;
  const selectedStartDate = useMemo(() => parseIsoDate(activeSelectedStart), [activeSelectedStart]);
  const selectedEndDate = useMemo(() => parseIsoDate(activeSelectedEnd), [activeSelectedEnd]);

  const initialMonth = useMemo(() => {
    if (selectedStartDate) {
      return toUtcDate(selectedStartDate.getUTCFullYear(), selectedStartDate.getUTCMonth(), 1);
    }
    const today = new Date();
    return toUtcDate(today.getUTCFullYear(), today.getUTCMonth(), 1);
  }, [selectedStartDate]);

  const [month, setMonth] = useState(initialMonth);

  useEffect(() => {
    setMonth(getClampedCalendarMonth(initialMonth, minDateParsed, maxDateParsed));
  }, [initialMonth, maxDateParsed, minDateParsed]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setDraftRange(null);
      setMonthYearPickerOpen(false);
    }
  };

  const weekdayLabels = useMemo(
    () => getWeekdayLabels(locale, weekStartsOn),
    [locale, weekStartsOn]
  );

  const calendarDays = useMemo(() => {
    const firstOfMonth = toUtcDate(month.getUTCFullYear(), month.getUTCMonth(), 1);
    const start = startOfUtcWeek(firstOfMonth, weekStartsOn);
    return Array.from({ length: TOTAL_DAYS }, (_, index) => addUtcDays(start, index));
  }, [month, weekStartsOn]);

  const monthOptions = useMemo(() => getMonthOptions(locale), [locale]);
  const yearOptions = useMemo(() => {
    const currentYear = month.getUTCFullYear();
    const minYear = minDateParsed?.getUTCFullYear() ?? currentYear - DEFAULT_YEAR_OPTION_LOOKAROUND;
    const maxYear = maxDateParsed?.getUTCFullYear() ?? currentYear + DEFAULT_YEAR_OPTION_LOOKAROUND;

    if (maxYear < minYear) {
      return [currentYear];
    }

    return Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);
  }, [maxDateParsed, minDateParsed, month]);
  const monthLabel = getMonthLabel(month, locale);
  const previousMonthDisabled =
    disabled ||
    !isCalendarMonthInBounds(
      month.getUTCFullYear(),
      month.getUTCMonth() - 1,
      minDateParsed,
      maxDateParsed
    );
  const nextMonthDisabled =
    disabled ||
    !isCalendarMonthInBounds(
      month.getUTCFullYear(),
      month.getUTCMonth() + 1,
      minDateParsed,
      maxDateParsed
    );
  const setCalendarMonth = (year: number, monthIndex: number) => {
    setMonth(
      getClampedCalendarMonth(
        getCalendarMonthForParts(year, monthIndex),
        minDateParsed,
        maxDateParsed
      )
    );
  };

  const handleSelectDate = (date: Date) => {
    const dateKey = toIsoDate(date);
    if (!dateKey) return;

    const isBeforeMin = minDateParsed ? date < minDateParsed : false;
    const isAfterMax = maxDateParsed ? date > maxDateParsed : false;
    if (isBeforeMin || isAfterMax) {
      return;
    }

    const dateContext = {
      date,
      dateKey,
      selectedStart: activeSelectedStart,
      selectedEnd: activeSelectedEnd,
      draftStart: draftRange?.start ?? null,
      draftEnd: draftRange?.end ?? null,
    };

    if (isDateSelectable && !isDateSelectable(dateContext)) {
      return;
    }

    const resolvedRange = resolveRangeForDate?.(dateContext) ?? null;
    if (resolvedRange) {
      if (resolvedRange.preview && draftRange) {
        setDraftRange(null);
        onRangeSelect(
          resolvedRange.start < draftRange.start ? resolvedRange.start : draftRange.start,
          resolvedRange.end > draftRange.end ? resolvedRange.end : draftRange.end
        );
        setOpen(false);
        return;
      }

      if (resolvedRange.preview) {
        setDraftRange({ start: resolvedRange.start, end: resolvedRange.end });
        if (resolvedRange.close ?? closeOnRangeSelect) {
          setOpen(false);
        }
        return;
      }

      setDraftRange(null);
      onRangeSelect(resolvedRange.start, resolvedRange.end);
      if (resolvedRange.close ?? closeOnRangeSelect) {
        setOpen(false);
      }
      return;
    }

    if (!selectedStartDate || !selectedEndDate) {
      onRangeSelect(dateKey, dateKey);
      return;
    }

    const isSingleSelection = isSameDay(selectedStartDate, selectedEndDate);
    if (isSingleSelection) {
      if (isSameDay(date, selectedStartDate)) {
        onClear();
        return;
      }
      const startKey = toIsoDate(selectedStartDate);
      if (date < selectedStartDate) {
        onRangeSelect(dateKey, startKey);
      } else {
        onRangeSelect(startKey, dateKey);
      }
      return;
    }

    onRangeSelect(dateKey, dateKey);
  };

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        {onPrevious ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-8 px-0"
            onClick={onPrevious}
            disabled={disabled || previousDisabled}
            aria-label={labels.previous}
            data-testid="date-range-previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        ) : null}

        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn(
                'w-full min-w-0 justify-center text-center whitespace-normal break-words text-sm font-medium leading-tight',
                triggerClassName
              )}
              data-testid="date-range-trigger"
              aria-label={labels.monthPickerLabel}
              disabled={disabled}
            >
              <span className="flex min-w-0 flex-col items-center gap-0 leading-none">
                <span className="min-w-0 break-words text-sm font-medium">{label}</span>
                {modeLabel ? (
                  <span className="-mt-0.5 text-sm font-medium text-muted-foreground">
                    {modeLabel}
                  </span>
                ) : null}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              'w-[calc(100vw-1rem)] max-w-[34rem] overflow-hidden rounded-none border border-border p-0 shadow-lg',
              popoverClassName
            )}
            side="bottom"
            align="center"
            sideOffset={8}
            collisionPadding={16}
            avoidCollisions
            data-testid="date-range-popover"
          >
            <div className="grid max-h-[70vh] grid-cols-1 overflow-hidden sm:max-h-[80vh] sm:grid-cols-[14rem_minmax(0,1fr)]">
              <div className="border-b border-border bg-muted/20 p-4 sm:border-b-0 sm:border-r">
                <div className="max-h-[16rem] space-y-4 overflow-y-auto pr-1 sm:max-h-[22rem]">
                  {modeOptions.length > 0 && onModeSelect ? (
                    <div>
                      <Text
                        size="xs"
                        className="mb-3 font-semibold tracking-wide text-muted-foreground"
                      >
                        {labels.modeSectionTitle ?? DEFAULT_MODE_SECTION_TITLE}
                      </Text>
                      <div className="space-y-1">
                        {modeOptions.map((option) => {
                          const isActive = activeModeId === option.id;

                          return (
                            <Button
                              key={option.id}
                              type="button"
                              size="sm"
                              variant={isActive ? 'secondary' : 'ghost'}
                              className={cn(
                                'h-auto w-full justify-start rounded-none px-3 py-2 whitespace-normal text-left leading-snug',
                                isActive && 'shadow-none'
                              )}
                              onClick={() => {
                                if (disabled) return;
                                setDraftRange(null);
                                onModeSelect(option.id);
                              }}
                              disabled={disabled}
                              data-testid={`date-range-mode-${option.id}`}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {quickFilters.length > 0 || showClear ? (
                    <div>
                      <Text
                        size="xs"
                        className="mb-3 font-semibold tracking-wide text-muted-foreground"
                      >
                        {labels.quickFiltersTitle}
                      </Text>
                      <div className="space-y-1">
                        {quickFilters.map((filter) => {
                          const isActive = activeQuickFilterId === filter.id;
                          return (
                            <Button
                              key={filter.id}
                              type="button"
                              size="sm"
                              variant={isActive ? 'secondary' : 'ghost'}
                              className={cn(
                                'h-auto w-full justify-start rounded-none px-3 py-2 whitespace-normal text-left leading-snug',
                                isActive && 'shadow-none'
                              )}
                              onClick={() => {
                                if (disabled) return;
                                setDraftRange(null);
                                onQuickFilterSelect(filter.id);
                                setOpen(false);
                              }}
                              disabled={disabled}
                              data-testid={`date-range-quick-${filter.id}`}
                            >
                              {filter.label}
                            </Button>
                          );
                        })}
                        {showClear ? (
                          <Button
                            type="button"
                            size="sm"
                            variant={clearActive ? 'secondary' : 'ghost'}
                            className="h-auto w-full justify-start whitespace-normal rounded-none px-3 py-2 text-left leading-snug"
                            onClick={() => {
                              if (disabled) return;
                              setDraftRange(null);
                              onClear();
                              setOpen(false);
                            }}
                            disabled={disabled}
                            data-testid="date-range-clear"
                          >
                            {labels.clear}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="bg-background p-4">
                <div className="mb-4 flex items-center justify-between">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 rounded-none"
                    aria-label={labels.previous}
                    onClick={() => {
                      if (disabled) return;
                      setMonth((current) =>
                        getClampedCalendarMonth(
                          toUtcDate(current.getUTCFullYear(), current.getUTCMonth() - 1, 1),
                          minDateParsed,
                          maxDateParsed
                        )
                      );
                    }}
                    disabled={previousMonthDisabled}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-10 min-w-0 rounded-none px-3 text-sm font-semibold"
                    aria-label={`${labels.monthPickerLabel}: ${monthLabel}`}
                    aria-expanded={monthYearPickerOpen}
                    onClick={() => {
                      if (disabled) return;
                      setMonthYearPickerOpen((current) => !current);
                    }}
                    disabled={disabled}
                    data-testid="date-range-month-year-trigger"
                  >
                    <span className="min-w-0 truncate">{monthLabel}</span>
                    <ChevronDown
                      className={cn(
                        'ml-1 h-4 w-4 shrink-0 transition-transform',
                        monthYearPickerOpen && 'rotate-180'
                      )}
                    />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 rounded-none"
                    aria-label={labels.next}
                    onClick={() => {
                      if (disabled) return;
                      setMonth((current) =>
                        getClampedCalendarMonth(
                          toUtcDate(current.getUTCFullYear(), current.getUTCMonth() + 1, 1),
                          minDateParsed,
                          maxDateParsed
                        )
                      );
                    }}
                    disabled={nextMonthDisabled}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                {monthYearPickerOpen ? (
                  <div className="mb-4 grid grid-cols-2 gap-2">
                    <div className="min-w-0">
                      <label htmlFor={monthSelectId} className="sr-only">
                        {labels.monthSelectLabel ?? DEFAULT_MONTH_SELECT_LABEL}
                      </label>
                      <select
                        id={monthSelectId}
                        className="h-11 w-full rounded-none border border-border bg-background px-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                        value={String(month.getUTCMonth())}
                        onChange={(event) => {
                          setCalendarMonth(month.getUTCFullYear(), Number(event.target.value));
                        }}
                        disabled={disabled}
                        data-testid="date-range-month-select"
                      >
                        {monthOptions.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={
                              !isCalendarMonthInBounds(
                                month.getUTCFullYear(),
                                Number(option.value),
                                minDateParsed,
                                maxDateParsed
                              )
                            }
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label htmlFor={yearSelectId} className="sr-only">
                        {labels.yearSelectLabel ?? DEFAULT_YEAR_SELECT_LABEL}
                      </label>
                      <select
                        id={yearSelectId}
                        className="h-11 w-full rounded-none border border-border bg-background px-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                        value={String(month.getUTCFullYear())}
                        onChange={(event) => {
                          setCalendarMonth(Number(event.target.value), month.getUTCMonth());
                        }}
                        disabled={disabled}
                        data-testid="date-range-year-select"
                      >
                        {yearOptions.map((year) => (
                          <option key={year} value={String(year)}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : null}
                <div className="grid grid-cols-7 gap-1 text-xs text-muted-foreground">
                  {weekdayLabels.map((day) => (
                    <div key={day} className="py-1 text-center">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-7 gap-1.5">
                  {calendarDays.map((day) => {
                    const dayKey = toIsoDate(day);
                    const isInMonth = day.getUTCMonth() === month.getUTCMonth();
                    const isSelectable =
                      (!minDateParsed || day >= minDateParsed) &&
                      (!maxDateParsed || day <= maxDateParsed) &&
                      (isDateSelectable
                        ? isDateSelectable({
                            date: day,
                            dateKey: dayKey,
                            selectedStart: activeSelectedStart,
                            selectedEnd: activeSelectedEnd,
                            draftStart: draftRange?.start ?? null,
                            draftEnd: draftRange?.end ?? null,
                          })
                        : true);
                    const isSelectedStart = selectedStartDate
                      ? dayKey === toIsoDate(selectedStartDate)
                      : false;
                    const isSelectedEnd = selectedEndDate
                      ? dayKey === toIsoDate(selectedEndDate)
                      : false;
                    const isInRange =
                      selectedStartDate && selectedEndDate
                        ? day >= selectedStartDate && day <= selectedEndDate
                        : false;

                    return (
                      <button
                        key={dayKey}
                        type="button"
                        onClick={() => {
                          if (disabled) return;
                          handleSelectDate(day);
                        }}
                        disabled={disabled || !isSelectable}
                        data-testid={`date-range-day-${dayKey}`}
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-none border border-transparent text-xs transition',
                          isInRange && 'bg-primary/12 text-primary',
                          (isSelectedStart || isSelectedEnd) &&
                            'border-primary bg-primary text-primary-foreground',
                          !isInRange && isSelectable && 'hover:border-border hover:bg-muted/60',
                          !isInMonth && 'text-muted-foreground/60',
                          !isSelectable && 'cursor-not-allowed opacity-40'
                        )}
                      >
                        {day.getUTCDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {onNext ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-8 px-0"
            onClick={onNext}
            disabled={disabled || nextDisabled}
            aria-label={labels.next}
            data-testid="date-range-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
